/**
 * Shared utilities for the GC OPTIMIZE™ Calculator.
 */

// ---------------------------------------------------------------------------
// Summary query-string key compression map
// Maps full formValues keys → short aliases for URL serialization.
// ---------------------------------------------------------------------------

const FV_KEY_MAP = {
  age:                      'a',
  status:                   's',
  treatmentGoals:           'tg',
  expectedStartDate:        'es',
  visitDate:                'vd',
  gcSelectedTypes:          'gt',
  gcDoses:                  'gd',
  gcReductionGoal:          'gr',
  gcDownTitrationExperience:'de',
  labDrawDate:              'ld',
  labDrawTime:              'lt',
  labDrawRelative:          'lr',
  clinicalAssessmentDate:   'cd',
  a4Assessment:             'a4',
  gcWithdrawalSeverity:     'ws',
  clinicalOptions:          'co',
  assessLabDrawDate:        'al',
  assessClinicalDate:       'ac',
};

// Reverse map: short → full
const FV_KEY_REVERSE = Object.fromEntries(
  Object.entries(FV_KEY_MAP).map(([full, short]) => [short, full])
);

/**
 * Compress formValues keys for URL serialization.
 */
export function compressFvKeys(fv) {
  if (!fv || typeof fv !== 'object') return fv;
  const out = {};
  for (const [key, value] of Object.entries(fv)) {
    out[FV_KEY_MAP[key] || key] = value;
  }
  return out;
}

/**
 * Expand compressed formValues keys back to full names.
 */
export function expandFvKeys(fv) {
  if (!fv || typeof fv !== 'object') return fv;
  const out = {};
  for (const [key, value] of Object.entries(fv)) {
    out[FV_KEY_REVERSE[key] || key] = value;
  }
  return out;
}

/**
 * Parses a mm/dd/yyyy date string into a local Date object.
 * Returns null if the string is missing, malformed, or not a real calendar date.
 *
 * @param {string} str — date string in mm/dd/yyyy format
 * @returns {Date|null}
 */
export function parseMDY(str) {
  if (!isValidDate(str)) return null;
  const [mm, dd, yyyy] = str.split('/').map(Number);
  return new Date(yyyy, mm - 1, dd);
}

/**
 * Returns the date `days` after the given mm/dd/yyyy date string,
 * formatted as mm/dd/yyyy. Returns an empty string if the input is invalid.
 *
 * @param {string} dateStr — date string in mm/dd/yyyy format
 * @param {number} [days=28] — number of days to add
 * @returns {string}
 */
export function addDaysToDate(dateStr, days = 28) {
  const date = parseMDY(dateStr);
  if (!date) return '';
  const result = new Date(date);
  result.setDate(date.getDate() + days);
  const mm = String(result.getMonth() + 1).padStart(2, '0');
  const dd = String(result.getDate()).padStart(2, '0');
  return `${mm}/${dd}/${result.getFullYear()}`;
}

/**
 * Checks if a mm/dd/yyyy string represents a real calendar date.
 * Rejects invalid months, days (e.g. 02/30), and years before 1900.
 */
export function isValidDate(val) {
  if (!val || !/^\d{2}\/\d{2}\/\d{4}$/.test(val)) return false;
  const [mm, dd, yyyy] = val.split('/').map(Number);
  if (yyyy < 1900 || mm < 1 || mm > 12 || dd < 1) return false;
  const date = new Date(yyyy, mm - 1, dd);
  // Verify the date didn't roll over (e.g. Feb 30 → Mar 2)
  return date.getFullYear() === yyyy && date.getMonth() === mm - 1 && date.getDate() === dd;
}

/**
 * Validates a date string (mm/dd/yyyy) is not in the future.
 * Calls `calendarRef.current.setInvalid(true/false)` to toggle
 * the calendar input's built-in error display.
 *
 * @param {string} val        — date string in mm/dd/yyyy format
 * @param {Object} calendarRef — React ref to a FormInputCalendar instance
 * @returns {boolean} true if valid (not future), false if future date
 */
export function validatePastDate(val, calendarRef) {
  if (val && /^\d{2}\/\d{2}\/\d{4}$/.test(val)) {
    if (!isValidDate(val)) {
      calendarRef.current?.setInvalid(true);
      return false;
    }
    const [mm, dd, yyyy] = val.split('/');
    const entered = new Date(+yyyy, +mm - 1, +dd);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (entered >= today) {
      calendarRef.current?.setInvalid(true);
      return false;
    }
  }
  calendarRef.current?.setInvalid(false);
  return true;
}

/**
 * Validates a date string (mm/dd/yyyy) is within the past 5 years.
 * Requires a strictly past date (today is invalid), and rejects dates older than 5 years.
 *
 * @param {string} val        — date string in mm/dd/yyyy format
 * @param {Object} calendarRef — React ref to a FormInputCalendar instance
 * @returns {boolean} true if valid, false if today/future or older than 5 years
 */
export function validateWithin5Years(val, calendarRef) {
  if (val && /^\d{2}\/\d{2}\/\d{4}$/.test(val)) {
    if (!isValidDate(val)) {
      calendarRef.current?.setInvalid(true);
      return false;
    }
    const [mm, dd, yyyy] = val.split('/').map(Number);
    const entered = new Date(yyyy, mm - 1, dd);
    entered.setHours(0, 0, 0, 0);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Rolling boundary: exactly 5 calendar years back from today's date.
    const earliestAllowedDate = new Date(today);
    earliestAllowedDate.setFullYear(today.getFullYear() - 5);

    if (entered >= today || entered < earliestAllowedDate) {
      calendarRef.current?.setInvalid(true);
      return false;
    }
  }
  calendarRef.current?.setInvalid(false);
  return true;
}

/**
 * Validates a date string (mm/dd/yyyy) is today or in the future by default.
 * Set allowToday=false to require strictly future dates.
 *
 * @param {string} val - date string in mm/dd/yyyy format
 * @param {Object} calendarRef - React ref to a FormInputCalendar instance
 * @param {Object} options
 * @param {boolean} [options.allowToday=true] - whether today's date is valid
 * @returns {boolean}
 */
export function validatePresentOrFutureDate(val, calendarRef, { allowToday = true } = {}) {
  if (!val || !/^\d{2}\/\d{2}\/\d{4}$/.test(val)) {
    calendarRef.current?.setInvalid(false);
    return true;
  }

  if (!isValidDate(val)) {
    calendarRef.current?.setInvalid(true);
    return false;
  }

  const [mm, dd, yyyy] = val.split('/').map(Number);
  const entered = new Date(yyyy, mm - 1, dd);
  entered.setHours(0, 0, 0, 0);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const valid = allowToday ? entered >= today : entered > today;
  calendarRef.current?.setInvalid(!valid);
  return valid;
}

/**
 * Validates a date string against an optional reference date + day window.
 *
 * @param {string} val - Date string in mm/dd/yyyy format.
 * @param {Object} calendarRef - React ref to FormInputCalendar instance.
 * @param {Object} options
 * @param {string} [options.referenceDate] - Reference date (mm/dd/yyyy) for window comparison.
 * @param {number} [options.minDays=28] - Minimum allowed day difference (entered - reference).
 * @param {number} [options.maxDays=84] - Maximum allowed day difference (entered - reference).
 * @param {boolean} [options.requirePastDate=false] - If true, runs validatePastDate first.
 * @param {boolean} [options.allowMissingReference=true] - If true and no valid referenceDate, only base validation is applied.
 * @returns {boolean}
 */
export function validateDateWithinDayWindow(
  val,
  calendarRef,
  {
    referenceDate = '',
    minDays = 28,
    maxDays = 84,
    requirePastDate = false,
    allowMissingReference = true,
    warningOnly = false,
  } = {}
) {
  const markInvalid = (flag) => {
    if (warningOnly) {
      calendarRef.current?.setWarning(flag);
    } else {
      calendarRef.current?.setInvalid(flag);
    }
  };

  if (!val || !/^\d{2}\/\d{2}\/\d{4}$/.test(val)) {
    markInvalid(false);
    return true;
  }

  if (!isValidDate(val)) {
    markInvalid(true);
    return false;
  }

  if (requirePastDate && !validatePastDate(val, calendarRef)) {
    return false;
  }

  const hasValidRef = !!referenceDate && /^\d{2}\/\d{2}\/\d{4}$/.test(referenceDate) && isValidDate(referenceDate);
  if (!hasValidRef) {
    if (allowMissingReference) {
      markInvalid(false);
      return true;
    }
    markInvalid(true);
    return false;
  }

  const [mm, dd, yyyy] = val.split('/').map(Number);
  const [rmm, rdd, ryyyy] = referenceDate.split('/').map(Number);
  const entered = new Date(yyyy, mm - 1, dd);
  const ref = new Date(ryyyy, rmm - 1, rdd);
  const diffDays = (entered - ref) / (1000 * 60 * 60 * 24);
  const inWindow = diffDays >= minDays && diffDays <= maxDays;

  markInvalid(!inWindow);
  return inWindow;
}
